/**
 * Contexte d'authentification de la console.
 *
 * - `AuthProvider` : reprend la session au démarrage (refresh token en
 *   `localStorage` → `POST /v1/auth/refresh` → access token en mémoire), expose
 *   `login` / `logout`.
 * - `useAuth` : accès au statut + à l'utilisateur courant.
 *
 * Le rôle affiché est dérivé de l'access token (`api.me()`), doublé du rôle
 * renvoyé par `/login`. La décision d'accès reste appliquée côté API.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { lireCapacites, type LoginResponse, type UserRole } from '@silex/shared';

import { api, setOnAuthLost } from '../api/client';
import { tokenStore } from '../api/tokens';
import { ancienParcoursEnPause } from '../lib/manuel-vivant';

export interface AuthUser {
  readonly id: string;
  readonly role: UserRole;
  readonly tenantId: string;
  /** E-mail saisi à la connexion (confort d'affichage), sinon `null`. */
  readonly email: string | null;
  readonly mfaActif: boolean;
  /**
   * AB#296 — le rôle impose le double facteur et le compte ne l'a pas enrôlé.
   * `RequireAuth` montre alors l'écran d'enrôlement, et rien d'autre.
   */
  readonly mfaEnrolementRequis: boolean;
}

type AuthStatus = 'loading' | 'authenticated' | 'anonymous';

interface AuthContextValue {
  status: AuthStatus;
  user: AuthUser | null;
  /**
   * Lève `ApiError` (code `MFA_REQUIRED` si un OTP est attendu). Renvoie
   * l'utilisateur connecté : l'appelant en a besoin tout de suite pour choisir
   * l'écran d'accueil, avant que l'état React n'ait été repropagé.
   */
  login: (email: string, password: string, otp?: string) => Promise<AuthUser>;
  /**
   * AB#296 — l'enrôlement est confirmé : l'API a révoqué les sessions
   * antérieures et rendu une session neuve, qui remplace la courante.
   */
  terminerEnrolementMfa: (res: LoginResponse) => void;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/** Libellé lisible d'un rôle, pour l'en-tête. */
export function roleLabel(role: UserRole): string {
  switch (role) {
    case 'admin':
      return 'Administration';
    case 'tuteur':
      return 'Tuteur';
    case 'parent':
      return 'Parent';
    case 'eleve':
      return 'Élève';
    default:
      return role;
  }
}

/**
 * Où atterrit un compte après connexion. La console ouvre sur la file de
 * travail du tuteur ; un compte `admin` n'a pas de file (`/v1/tuteur/*` lui
 * répond 403) et atterrit sur l'écran d'administration.
 */
export function accueilPourRole(role: UserRole): string {
  if (role !== 'admin') return '/ma-journee';
  /* Refonte de la console, L1 : avec le menu par tâches, l'administrateur
     atterrit sur « Aujourd'hui », un écran qui charge et dit quoi faire — et
     non plus sur un onglet d'administration. Rail d'avant : l'accueil d'avant. */
  return ancienParcoursEnPause() ? '/aujourdhui' : '/administration';
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [user, setUser] = useState<AuthUser | null>(null);
  const mounted = useRef(true);

  const clearSession = useCallback(() => {
    tokenStore.clear();
    if (mounted.current) {
      setUser(null);
      setStatus('anonymous');
    }
  }, []);

  // Session perdue en cours de route (refresh impossible sur un 401).
  useEffect(() => {
    setOnAuthLost(clearSession);
    return () => setOnAuthLost(() => {});
  }, [clearSession]);

  // Reprise de session au montage.
  useEffect(() => {
    mounted.current = true;
    let cancelled = false;

    async function restore() {
      if (!tokenStore.getRefreshToken()) {
        if (!cancelled) setStatus('anonymous');
        return;
      }
      const ok = await api.refresh();
      if (cancelled) return;
      const claims = ok ? api.me() : null;
      if (!ok || !claims) {
        clearSession();
        return;
      }
      setUser({
        id: claims.sub,
        role: claims.role,
        tenantId: claims.tenantId,
        email: tokenStore.getIdentity(),
        mfaActif: false,
        mfaEnrolementRequis: claims.mfaEnrolementRequis === true,
      });
      setStatus('authenticated');
    }

    void restore();
    return () => {
      cancelled = true;
      mounted.current = false;
    };
  }, [clearSession]);

  const login = useCallback(async (email: string, password: string, otp?: string) => {
    const trimmed = email.trim();
    const res = await api.login({
      email: trimmed,
      password,
      ...(otp ? { otp: otp.trim() } : {}),
    });
    tokenStore.setAccessToken(res.tokens.accessToken);
    tokenStore.setRefreshToken(res.tokens.refreshToken);
    tokenStore.setIdentity(trimmed);
    /* MV-SOC-01 — le second étage du drapeau du manuel vivant, lu par
       `lib/manuel-vivant.ts`. À la reprise de session c'est le renouvellement
       de jeton qui le pose (`api.refresh`). */
    tokenStore.setCapacites(lireCapacites(res.capacites));
    const claims = api.me();
    const connecte: AuthUser = {
      id: res.user.id,
      role: claims?.role ?? res.user.role,
      tenantId: claims?.tenantId ?? res.user.id,
      email: trimmed,
      mfaActif: res.user.mfaActif,
      mfaEnrolementRequis: res.user.mfaEnrolementRequis === true,
    };
    setUser(connecte);
    setStatus('authenticated');
    return connecte;
  }, []);

  const terminerEnrolementMfa = useCallback((res: LoginResponse) => {
    tokenStore.setAccessToken(res.tokens.accessToken);
    tokenStore.setRefreshToken(res.tokens.refreshToken);
    // La session neuve rend ses capacités, comme `/login` (MV-SOC-01).
    tokenStore.setCapacites(lireCapacites(res.capacites));
    const claims = api.me();
    setUser((precedent) => ({
      id: res.user.id,
      role: claims?.role ?? res.user.role,
      tenantId: claims?.tenantId ?? precedent?.tenantId ?? res.user.id,
      email: precedent?.email ?? tokenStore.getIdentity(),
      mfaActif: true,
      mfaEnrolementRequis: false,
    }));
  }, []);

  const logout = useCallback(async () => {
    await api.logout();
    setUser(null);
    setStatus('anonymous');
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ status, user, login, terminerEnrolementMfa, logout }),
    [status, user, login, terminerEnrolementMfa, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Le rôle de la session, ou `null` hors de `<AuthProvider>` (un composant
 * rendu seul, dans un test). Ne lève jamais, contrairement à `useAuth`.
 */
export function useRoleCourant(): UserRole | null {
  return useContext(AuthContext)?.user?.role ?? null;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth doit être utilisé dans <AuthProvider>');
  return ctx;
}
